import React, { useState } from 'react';
import { Search, AlertTriangle, CheckCircle2, Loader2, Link2, LogIn, Wrench } from 'lucide-react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';

const API = 'https://api.socialness.ai/management';

interface Verdict {
  severity: 'high' | 'medium' | 'ok';
  cause: string;
  fix: string;
}

interface OrgBrand {
  brandName: string;
  role: string;
  status: string;
}

interface OrgSubscription {
  plan: string;
  planName: string;
  billing: string;
  endDate: string;
}

interface DiagnoseResult {
  email: string;
  checks: {
    account?: { exists: boolean; uid?: string; name?: string; createdAt?: string; lastLoginAt?: string; emailVerified?: boolean; disabled?: boolean };
    invitations?: Array<{ id: string; status: string; brandId: string; role: string; orgRole: string }>;
    invitationsSent?: Array<{ id: string; invitedEmail: string; status: string; brandId: string; role: string }>;
    brandsReal?: Array<{ brandId: string; name: string }>;
    orgs?: Array<{
      orgId: string;
      name: string;
      relation: string;
      membershipStatus?: string;
      role: string;
      orgStatus: string;
      entitled: boolean;
      subscription?: OrgSubscription;
      brands?: OrgBrand[];
      memberUserId?: string;
      userIdMatches?: boolean;
    }>;
  };
  verdicts: Verdict[];
}

const fmtDate = (s?: string) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
};

const fmtSub = (s?: OrgSubscription) => {
  if (!s) return '—';
  const plan = s.planName || s.plan || '—';
  const parts = [plan];
  if (s.billing) parts.push(s.billing);
  if (s.endDate) parts.push(`échéance ${fmtDate(s.endDate)}`);
  return parts.join(' · ');
};

const severityStyle: Record<Verdict['severity'], string> = {
  high: 'border-red-200 bg-red-50',
  medium: 'border-amber-200 bg-amber-50',
  ok: 'border-green-200 bg-green-50',
};

const AdminDiagnose = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<DiagnoseResult | null>(null);

  // Rattachement à une org
  const [attachOrgId, setAttachOrgId] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [attachMsg, setAttachMsg] = useState('');

  // Impersonation
  const [impersonating, setImpersonating] = useState(false);

  // Réparation du rattachement (placeholder userId → vrai uid)
  const [repairing, setRepairing] = useState(false);
  const [repairMsg, setRepairMsg] = useState('');

  const token = async () => {
    const t = await auth.currentUser?.getIdToken();
    if (!t) throw new Error('Session expirée, reconnecte-toi.');
    return t;
  };

  const runDiagnosis = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setAttachMsg('');
    setResult(null);
    const target = email.trim().toLowerCase();
    if (!target) return;

    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/diagnose-user?email=${encodeURIComponent(target)}`, {
        headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json' },
      });
      if (res.status === 403) throw new Error("Accès refusé : ce compte n'est pas admin.");
      if (!res.ok) throw new Error(`Erreur serveur (${res.status}).`);
      setResult(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue.');
    } finally {
      setLoading(false);
    }
  };

  const impersonate = async () => {
    const uid = result?.checks.account?.uid;
    if (!uid || !result) return;
    if (
      !window.confirm(
        `Se connecter EN TANT QUE ${result.email} ?\n\n` +
          `⚠️ Ça remplace ta session admin actuelle (tu seras déconnecté de ton compte et reconnecté en tant que cet utilisateur). ` +
          `Pour ne pas perdre ta session admin, fais-le plutôt dans une fenêtre privée/incognito.`
      )
    )
      return;
    setImpersonating(true);
    try {
      const res = await fetch(`${API}/admin/impersonate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) throw new Error(data?.error || `Échec (${res.status}).`);
      await signInWithCustomToken(auth, data.token);
      window.location.href = '/'; // recharge l'app en tant que l'utilisateur
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Échec de la connexion en tant que cet utilisateur.');
      setImpersonating(false);
    }
  };

  const repair = async () => {
    if (!result) return;
    if (!window.confirm(`Réparer le rattachement de ${result.email} ?\n\nÇa ré-écrit le userId temporaire (placeholder) vers son vrai uid Firebase dans l'organisation et les marques.`))
      return;
    setRepairing(true);
    setRepairMsg('');
    try {
      const res = await fetch(`${API}/admin/repair-membership`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: result.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Échec (${res.status}).`);
      setRepairMsg(
        `${data.message} (uid ${data.realUid} · org-membres corrigés: ${data.orgMembersFixed?.length ?? 0} · marques corrigées: ${data.brandsFixed?.length ?? 0})`
      );
      await runDiagnosis(); // refresh
    } catch (err) {
      setRepairMsg(err instanceof Error ? err.message : 'Erreur inconnue.');
    } finally {
      setRepairing(false);
    }
  };

  const attach = async () => {
    const orgId = attachOrgId.trim();
    if (!orgId || !result) return;
    if (!window.confirm(`Rattacher ${result.email} à l'organisation ${orgId} (membre actif) ?`)) return;
    setAttaching(true);
    setAttachMsg('');
    try {
      const res = await fetch(`${API}/admin/attach-to-org`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: result.email, orgId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Échec (${res.status}).`);
      setAttachMsg(
        data.alreadyMember
          ? `Déjà membre de « ${data.orgName} ».`
          : `✅ Rattaché à « ${data.orgName} ». L'accès devrait être débloqué.`
      );
      setAttachOrgId('');
      await runDiagnosis(); // refresh
    } catch (err) {
      setAttachMsg(err instanceof Error ? err.message : 'Erreur inconnue.');
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900">Diagnostic utilisateur</h1>
      <p className="mt-1 text-sm text-gray-500">
        Tape l'email d'un client / collègue invité pour savoir pourquoi il n'arrive pas à entrer dans l'app.
      </p>

      <form onSubmit={runDiagnosis} className="mt-6 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="collegue@exemple.com"
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Diagnostiquer
        </button>
      </form>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 space-y-6">
          {/* Verdicts */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Verdict</h2>
            {result.verdicts.map((v, i) => (
              <div key={i} className={`rounded-lg border p-4 ${severityStyle[v.severity]}`}>
                <div className="flex items-start gap-2">
                  {v.severity === 'ok' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{v.cause}</p>
                    <p className="mt-1 text-sm text-gray-600">
                      <span className="font-semibold">Fix : </span>
                      {v.fix}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Réparer le rattachement (placeholder userId → vrai uid) — affiché
              uniquement quand un mismatch est détecté (sinon rien à réparer) */}
          {result.checks.account?.exists &&
            (result.checks.orgs?.some((o) => o.userIdMatches === false) ||
              (result.checks.orgs?.some((o) => (o.brands?.length || 0) > 0) &&
                (result.checks.brandsReal?.length || 0) === 0)) && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Wrench className="h-4 w-4" />
                    Réparer le rattachement
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    Ré-écrit le <span className="font-mono">userId</span> temporaire (placeholder) vers le vrai uid Firebase dans l'org et les marques.
                    À utiliser quand « câblé sur un autre userId » apparaît dans le verdict.
                  </p>
                </div>
                <button
                  onClick={repair}
                  disabled={repairing}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex-shrink-0"
                >
                  {repairing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                  Réparer
                </button>
              </div>
              {repairMsg && <p className="mt-2 text-sm text-gray-700">{repairMsg}</p>}
            </div>
          )}

          {/* Détails */}
          <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 text-sm">
            {/* Compte + activité */}
            <div className="p-3">
              <div className="text-gray-500 mb-1">Compte & activité</div>
              {result.checks.account?.exists ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-0.5 text-gray-700">
                    <div>
                      <span className="font-medium">{result.checks.account.name || '(sans nom)'}</span>{' '}
                      <span className="font-mono text-xs text-gray-400">{result.checks.account.uid}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Créé le {fmtDate(result.checks.account.createdAt)} · Dernière connexion {fmtDate(result.checks.account.lastLoginAt)}
                    </div>
                    {(result.checks.account.emailVerified !== undefined || result.checks.account.disabled !== undefined) && (
                      <div className="text-xs text-gray-500">
                        Email {result.checks.account.emailVerified ? '✅ vérifié' : '⚠️ non vérifié'}
                        {result.checks.account.disabled ? ' · ❌ compte désactivé' : ' · compte actif'}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={impersonate}
                    disabled={impersonating}
                    title="Se connecter en tant que cet utilisateur (fais-le en navigation privée)"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
                  >
                    {impersonating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}
                    Se connecter en tant que
                  </button>
                </div>
              ) : (
                <span className="text-red-600">❌ aucun compte</span>
              )}
            </div>

            {/* Invitations reçues */}
            <div className="p-3">
              <div className="text-gray-500 mb-1">Invitations reçues</div>
              {result.checks.invitations?.length ? (
                result.checks.invitations.map((inv) => (
                  <div key={inv.id} className="font-mono text-xs text-gray-700">
                    {inv.id} · {inv.status} · {inv.role}/{inv.orgRole}
                  </div>
                ))
              ) : (
                <span className="text-gray-400">aucune</span>
              )}
            </div>

            {/* Invitations envoyées */}
            <div className="p-3">
              <div className="text-gray-500 mb-1">Invitations envoyées</div>
              {result.checks.invitationsSent?.length ? (
                result.checks.invitationsSent.map((inv) => (
                  <div key={inv.id} className="font-mono text-xs text-gray-700">
                    → {inv.invitedEmail} · {inv.status} · {inv.role}
                  </div>
                ))
              ) : (
                <span className="text-gray-400">aucune</span>
              )}
            </div>

            {/* Organisations liées + abonnement */}
            <div className="p-3">
              <div className="text-gray-500 mb-1">Organisations liées & abonnement</div>
              {result.checks.orgs?.length ? (
                <div className="space-y-2">
                  {result.checks.orgs.map((o) => (
                    <div key={o.orgId} className="rounded border border-gray-100 bg-gray-50 p-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{o.name}</span>
                        <span className={o.entitled ? 'text-green-600 text-xs' : 'text-red-600 text-xs'}>
                          {o.entitled ? '✅ donne accès' : '❌ pas d’accès'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {o.relation} · rôle {o.role} · statut org {o.orgStatus}
                        {o.membershipStatus ? ` · membre ${o.membershipStatus}` : ''}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        <span className="font-semibold">Abonnement :</span> {fmtSub(o.subscription)}
                      </div>
                      <div className="font-mono text-[11px] text-gray-400 mt-0.5">{o.orgId}</div>
                      {o.userIdMatches === false && (
                        <div className="mt-1 text-xs text-red-600">
                          ⚠️ userId stocké <span className="font-mono">{o.memberUserId}</span> ≠ uid du compte → l'app ne voit pas cette adhésion (placeholder). Cliquer « Réparer ».
                        </div>
                      )}
                      {o.brands?.length ? (
                        <div className="mt-1 text-xs text-gray-600">
                          Brands (métadonnées) : {o.brands.map((b) => `${b.brandName} (${b.role}/${b.status})`).join(', ')}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400">aucune</span>
              )}
            </div>

            {/* Marques réelles vues par l'app (collection brands) */}
            <div className="p-3">
              <div className="text-gray-500 mb-1">Marques réelles (ce que l'app affiche)</div>
              {result.checks.brandsReal?.length ? (
                <div className="text-xs text-gray-700">{result.checks.brandsReal.map((b) => b.name || b.brandId).join(', ')}</div>
              ) : (
                <span className="text-amber-600 text-xs">aucune ⚠️ (l'app affichera « 0 marque »)</span>
              )}
            </div>
          </div>

          {/* Rattacher à une org (write) */}
          {result.checks.account?.exists && (
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Link2 className="h-4 w-4" />
                Rattacher à une organisation
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Colle l'<span className="font-mono">orgId</span> d'une org active (ex. celui affiché ci-dessus pour un collègue qui a accès)
                pour rattacher <span className="font-medium">{result.email}</span> comme membre actif.
              </p>
              <div className="mt-3 flex gap-2">
                <input
                  value={attachOrgId}
                  onChange={(e) => setAttachOrgId(e.target.value)}
                  placeholder="organisation_..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={attach}
                  disabled={attaching || !attachOrgId.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {attaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                  Rattacher
                </button>
              </div>
              {attachMsg && <p className="mt-2 text-sm text-gray-700">{attachMsg}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDiagnose;
